/**
 * ReportPage — the gated bug-report form (route `/report`, INSIDE ProtectedRoute).
 *
 * Story: epic-uat-portal-001-10.
 *
 * Six fields of a good bug report (design "decided shape"): the page URL, what
 * the tester did, what they expected, what actually happened, a grouped "any
 * errors" trio (page / console / network), and 0..6 screenshots. react-hook-form
 * + zod validate the text fields; screenshots are validated in component state
 * (count <= 6, each <= 5 MB, png/jpeg/webp only) with a clear message.
 *
 * A persistent VisibilityNotice (design D3) warns — before submit — that the body
 * and screenshots are shared with other testers.
 *
 * Submit outcomes (plan Task 4 Review Focus item 2):
 *   - 201 → show the returned bug id as a confirmation (form is replaced by a
 *     success panel with a "file another" reset).
 *   - 502 bug_capture_failed → a retryable error banner AND the typed field values
 *     are KEPT INTACT (we do NOT reset the form and do NOT switch to the success
 *     panel, so the uncontrolled RHF inputs retain their values and the tester can
 *     just press submit again). This is the "don't lose the report" guarantee.
 *   - 400 invalid_upload / Validation failed → a clear error message; fields kept.
 *   - 401 → authenticatedFetch has already cleared the token + redirected to
 *     /login; submitBug still throws so we never render a false success.
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  submitBug,
  BugSubmitApiError,
  type BugSubmitFields,
} from '@/services/bugSubmitService';
import { VisibilityNotice } from './components/VisibilityNotice';

/** Client-side screenshot limits (mirror the backend contract). */
export const MAX_SCREENSHOTS = 6;
// bl-1616-1 AC2: match the backend's multer cap EXACTLY (oracle-bridge
// UAT_MAX_UPLOAD_BYTES = 5_000_000 B). The previous 5 * 1024 * 1024 (5,242,880 B)
// let a file in the 5,000,000–5,242,880 B gap pass the client then 400 server-side
// (invalid_upload). 5,000,000 B is "5 MB" (decimal), so the copy stays truthful.
export const MAX_SCREENSHOT_BYTES = 5_000_000; // 5 MB per file (matches backend)
// bl-1616-1 AC1: the nginx ingress (bl-1616) caps the whole multipart body at 32m
// (33,554,432 B). Enforce the COMBINED screenshot size below that here so an
// oversized set is caught with a clear message BEFORE the network round-trip —
// otherwise nginx 413s at the edge and, its error page lacking CORS headers, the
// browser masks it as an unreadable rejected fetch (the bl-1616 failure mode).
// 30 MB = the backend's 6×5 MB file budget, leaving headroom under 32m for the
// seven text fields + multipart boundaries.
export const MAX_TOTAL_UPLOAD_BYTES = 30_000_000; // 30 MB combined
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ACCEPT_ATTR = 'image/png,image/jpeg,image/webp';

const schema = z.object({
  url: z
    .string()
    .min(1, 'Enter the page URL where you saw the problem')
    .url('Enter a valid URL, including https://'),
  did: z.string().min(1, 'Tell us what you did'),
  expected: z.string(),
  actual: z.string().min(1, 'Tell us what actually happened'),
  errorsPage: z.string(),
  errorsConsole: z.string(),
  errorsNetwork: z.string(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_VALUES: FormValues = {
  url: '',
  did: '',
  expected: '',
  actual: '',
  errorsPage: '',
  errorsConsole: '',
  errorsNetwork: '',
};

/** Validate a freshly-picked file list; returns a message or null if all good. */
function validateScreenshots(picked: File[]): string | null {
  if (picked.length > MAX_SCREENSHOTS) {
    return `Attach at most ${MAX_SCREENSHOTS} screenshots (you picked ${picked.length}).`;
  }
  // Combined-size check first: catch an over-budget set with a clear message before
  // it 413s at the ingress (bl-1616-1 AC1). Checked ahead of the per-file cap so an
  // oversized multi-file selection reports the real problem (too much total).
  const totalBytes = picked.reduce((sum, f) => sum + f.size, 0);
  if (totalBytes > MAX_TOTAL_UPLOAD_BYTES) {
    const mb = (totalBytes / 1_000_000).toFixed(1);
    const capMb = MAX_TOTAL_UPLOAD_BYTES / 1_000_000;
    return `The screenshots total ${mb} MB — the combined upload must be ${capMb} MB or less. Remove or shrink one.`;
  }
  const tooBig = picked.find((f) => f.size > MAX_SCREENSHOT_BYTES);
  if (tooBig) {
    return `"${tooBig.name}" is larger than 5 MB. Each screenshot must be 5 MB or less.`;
  }
  const wrongType = picked.find((f) => !ACCEPTED_IMAGE_TYPES.includes(f.type));
  if (wrongType) {
    return `"${wrongType.name}" isn't a PNG, JPEG, or WebP image.`;
  }
  return null;
}

export function ReportPage() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Remove all attached screenshots (and clear any stale pick error + the native input). */
  function clearFiles() {
    setFiles([]);
    setFileError(null);
    setFileNote(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    if (picked.length === 0) return; // cancelled dialog — leave current state
    const message = validateScreenshots(picked);
    if (message) {
      if (files.length > 0) {
        // There are already-accepted valid files — the bad pick is IGNORED and
        // does NOT block submit; just tell the tester their attachments are kept.
        setFileNote(
          `That selection was rejected (${message}) — your ${files.length} attached file${files.length === 1 ? '' : 's'} ${files.length === 1 ? 'is' : 'are'} still attached.`,
        );
        setFileError(null);
      } else {
        // Nothing valid to fall back on — block submit until it's resolved.
        setFileError(message);
        setFileNote(null);
      }
      return;
    }
    setFileError(null);
    setFileNote(null);
    setFiles(picked);
  }

  async function onSubmit(values: FormValues) {
    setSubmitError(null);
    setRetryable(false);
    // A blocking screenshot error (a bad pick with nothing valid to fall back on)
    // stops submit; a non-blocking fileNote (valid files kept despite a bad pick)
    // does not. The accepted `files` are valid-by-construction.
    if (fileError) return;

    const fields: BugSubmitFields = {
      url: values.url.trim(),
      did: values.did.trim(),
      expected: values.expected,
      actual: values.actual.trim(),
      errorsPage: values.errorsPage,
      errorsConsole: values.errorsConsole,
      errorsNetwork: values.errorsNetwork,
    };

    try {
      const { id } = await submitBug(fields, files);
      // Success: surface the id, clear the form + files for the next report.
      setSubmittedId(id);
      reset(EMPTY_VALUES);
      setFiles([]);
    } catch (err) {
      if (err instanceof BugSubmitApiError) {
        setSubmitError(err.message);
        // 502 is transient — invite a retry, and CRUCIALLY do not reset the form
        // or switch to the success panel, so every typed value stays on screen.
        setRetryable(err.status === 502);
      } else {
        // Not a BugSubmitApiError → the fetch was rejected before any readable HTTP
        // response (network drop, or an oversized body 413'd at the nginx edge whose
        // error page carries no CORS header, so the browser hands us an opaque
        // TypeError). Point at the likely cause instead of a bare generic. (bl-1616-1 AC3)
        setSubmitError(
          "Your report couldn't be sent. If you attached large screenshots, try removing some — otherwise check your connection and try again.",
        );
      }
    }
  }

  if (submittedId) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-16">
        <div
          role="status"
          data-testid="report-success"
          className="rounded border border-green-300 bg-green-50 px-5 py-6 text-center"
        >
          <h1 className="mb-2 text-2xl font-bold text-green-900">Report filed</h1>
          <p className="mb-1 text-green-900">Thanks — your report is in the queue.</p>
          <p className="mb-5 text-sm text-green-900">
            Reference:{' '}
            <span data-testid="report-id" className="font-mono font-semibold">
              {submittedId}
            </span>
          </p>
          <button
            type="button"
            data-testid="report-file-another"
            onClick={() => setSubmittedId(null)}
            className="rounded border border-green-400 px-4 py-2 font-medium text-green-900"
          >
            File another report
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-6 py-12">
      <h1 className="mb-1 text-2xl font-bold" data-testid="report-heading">
        Report a bug
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Tell us what happened. The more detail — URL, steps, errors, screenshots —
        the faster we can reproduce and fix it. New to testing?{' '}
        <Link to="/guide" data-testid="report-guide-link" className="underline">
          Read the full testing guide
        </Link>
        .
      </p>

      <div className="mb-6">
        <VisibilityNotice />
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-5"
        noValidate
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            Page URL <span className="text-red-600">*</span>
          </span>
          <input
            type="url"
            data-testid="report-url"
            className="rounded border border-gray-300 px-3 py-2"
            placeholder="https://app.staging.helloworlddao.com/…"
            {...register('url')}
          />
          {errors.url && (
            <span role="alert" data-testid="error-url" className="text-xs text-red-600">
              {errors.url.message}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            What you did <span className="text-red-600">*</span>
          </span>
          <textarea
            rows={3}
            data-testid="report-did"
            className="rounded border border-gray-300 px-3 py-2"
            placeholder="The steps you took, in order."
            {...register('did')}
          />
          {errors.did && (
            <span role="alert" data-testid="error-did" className="text-xs text-red-600">
              {errors.did.message}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">What you expected</span>
          <textarea
            rows={2}
            data-testid="report-expected"
            className="rounded border border-gray-300 px-3 py-2"
            placeholder="What you thought would happen."
            {...register('expected')}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            What actually happened <span className="text-red-600">*</span>
          </span>
          <textarea
            rows={3}
            data-testid="report-actual"
            className="rounded border border-gray-300 px-3 py-2"
            placeholder="What went wrong instead."
            {...register('actual')}
          />
          {errors.actual && (
            <span role="alert" data-testid="error-actual" className="text-xs text-red-600">
              {errors.actual.message}
            </span>
          )}
        </label>

        <fieldset
          data-testid="report-errors-group"
          className="flex flex-col gap-3 rounded border border-gray-200 px-4 py-3"
        >
          <legend className="px-1 text-sm font-medium">
            Any errors? <span className="font-normal text-muted-foreground">(optional)</span>
          </legend>
          <label className="flex flex-col gap-1 text-sm">
            <span>On the page</span>
            <textarea
              rows={2}
              data-testid="report-errors-page"
              className="rounded border border-gray-300 px-3 py-2"
              placeholder="Any error text shown on the page itself."
              {...register('errorsPage')}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>In the browser Console</span>
            <textarea
              rows={2}
              data-testid="report-errors-console"
              className="rounded border border-gray-300 px-3 py-2"
              placeholder="Red lines from DevTools → Console."
              {...register('errorsConsole')}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>In the Network tab</span>
            <textarea
              rows={2}
              data-testid="report-errors-network"
              className="rounded border border-gray-300 px-3 py-2"
              placeholder="Red / 4xx / 5xx rows from DevTools → Network."
              {...register('errorsNetwork')}
            />
          </label>
        </fieldset>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            Screenshots <span className="font-normal text-muted-foreground">(optional, up to {MAX_SCREENSHOTS})</span>
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            data-testid="report-screenshots"
            onChange={handleFileChange}
            className="text-sm"
          />
          <span className="text-xs text-muted-foreground">
            PNG, JPEG, or WebP. Up to {MAX_SCREENSHOTS} images, 5 MB each ({MAX_TOTAL_UPLOAD_BYTES / 1_000_000} MB total).
          </span>
          {(files.length > 0 || fileError) && (
            <span className="flex items-center gap-2 text-xs text-gray-700">
              {files.length > 0 && (
                <span data-testid="report-file-count">
                  {files.length} file{files.length === 1 ? '' : 's'} attached.
                </span>
              )}
              <button
                type="button"
                data-testid="report-clear-files"
                onClick={clearFiles}
                className="underline"
              >
                Remove screenshots
              </button>
            </span>
          )}
          {fileError && (
            <span role="alert" data-testid="error-screenshots" className="text-xs text-red-600">
              {fileError}
            </span>
          )}
          {fileNote && (
            <span data-testid="report-file-note" className="text-xs text-amber-700">
              {fileNote}
            </span>
          )}
        </label>

        {submitError && (
          <div
            role="alert"
            data-testid="report-error"
            className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            <p>{submitError}</p>
            {retryable && (
              <p data-testid="report-retry-hint" className="mt-1 text-xs">
                Your report is still here — just press “File report” to try again.
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="report-submit"
          className="self-start rounded bg-green-600 px-5 py-2.5 font-medium text-white disabled:opacity-60"
        >
          {isSubmitting ? 'Filing…' : 'File report'}
        </button>
      </form>
    </main>
  );
}
